import { useDispatch } from 'react-redux';
import { setBreadCrumbsData } from '../../store/redux/breadCrumbsData';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import SalesData from '../../modules/dashboard/components/SalesData';
import DurationTabs from '../../modules/dashboard/components/DurationTabs';
import { getApisData, handleDurationClick } from '../../modules/dashboard/utils/HelperFunctions';
import Loader from '../../modules/dashboard/components/Loader';
import VehiclesData from '../../modules/dashboard/components/VehiclesData';
import { dashboardDurations } from '../../config/FixedData';
import ModalContainer from '../../components/ModalContainer';
import CustomizeDateRange from '../../modules/dashboard/components/CustomizeDateRange';
import ListHeader from '../../components/ListHeader';
import Select from 'react-select';
import { customSelectStyles, customStyles } from '../../utils/SelectStyles';
import { hasPermission } from '../../utils/HelperFunctions';
import WelcomePage from './WelcomePage';
import useModelSelectOptions from '../../hooks/useModelSelectOptions';
import { makesSelectProps, modelsSelectProps, variantsSelectProps } from '../../utils/selectOptionsProps';
import InputSkeleton from '../../components/loaders/InputSkeleton';
import MonthlySales from '../../modules/dashboard/components/MonthlySales';
import SalesUsersData from '../../modules/dashboard/components/SalesUsersData';
import { useQuery } from '@tanstack/react-query';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // State management
  const [selectedBrand, setSelectedBrand] = useState<string>();
  const [selectedModel, setSelectedModel] = useState<string>();
  const [selectedVariant, setSelectedVariant] = useState<string>();
  const [activeTab, setActiveTab] = useState<string>('1 Year');
  const [selectedDuration, setSelectedDuration] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRange>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loadingApis, setLoadingApis] = useState<{ [key: string]: boolean }>({});

  // Computed values
  const dateDuration = useMemo(() => {
    if (!dateRange) return null;
    const diff = Math.ceil((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 3600 * 24) / 30);
    return diff <= 1 ? 'days' : diff <= 13 ? 'month' : 'years';
  }, [dateRange]);

  const { modelOptions: makesOptions, apiDataLoading: makesApiDataLoading } = useModelSelectOptions(makesSelectProps([{ label: t('all'), value: 'all' }]));
  const { modelOptions: modelsOptions, apiDataLoading: modelsApiDataLoading } = useModelSelectOptions(
    modelsSelectProps(selectedBrand || undefined, [{ label: t('all'), value: 'all' }])
  );
  const { modelOptions: variantsOptions, apiDataLoading: variantsApiDataLoading } = useModelSelectOptions(
    variantsSelectProps(selectedModel || undefined, [{ label: t('all'), value: 'all' }], i18n.language)
  );

  const { data: salesData, isLoading: salesDataLoading } = useQuery({
    ...getApisData('total-sales', i18n, selectedDuration, activeTab, 'total-sales', selectedModel, selectedVariant)
  });
  const { data: purchasesVehiclesData, isLoading: purchasesVehiclesDataLoading } = useQuery({
    ...getApisData('purchases-vehicles', i18n, selectedDuration, activeTab, 'total-vehicles-purchased', selectedModel, selectedVariant)
  });

  const { data: salesPerModelData, isLoading: salesPerModelDataLoading } = useQuery({
    ...getApisData('sales-per-model', i18n, selectedDuration, activeTab, 'sales-per-model', selectedModel, selectedVariant)
  });

  const { data: monthlySalesData, isLoading: monthlySalesDataLoading } = useQuery({
    ...getApisData('monthly-sales-count', i18n, selectedDuration, activeTab, 'monthly-sales-count', selectedModel, selectedVariant)
  });

  const { data: salesUsersData, isLoading: salesUsersDataLoading } = useQuery({
    ...getApisData('sales-users', i18n, selectedDuration, activeTab, 'sales-by-user', selectedModel, selectedVariant)
  });

  // Effects
  useEffect(() => {
    if (makesOptions?.length && !selectedBrand) {
      setSelectedBrand(makesOptions[1].value as string);
    }
  }, [makesOptions, selectedBrand]);

  useEffect(() => {
    const pageTitle = hasPermission(['dashboard.show']) ? t('dashboard') : t('home');
    dispatch(
      setBreadCrumbsData({
        links: [{ label: pageTitle, path: '/' }],
        page_title: pageTitle
      })
    );
  }, [t, dispatch]);

  useEffect(() => {
    handleDurationClick(activeTab, setSelectedDuration, dateRange);
  }, [activeTab, dateRange, dateDuration]);

  if (Object.values([salesDataLoading, purchasesVehiclesDataLoading, salesPerModelDataLoading, monthlySalesDataLoading, salesUsersDataLoading]).some(Boolean)) {
    return <Loader />;
  }

  if (!hasPermission(['dashboard.show'])) {
    return <WelcomePage />;
  }

  return (
    <div className="page_content service_details_page">
      <ListHeader customClass="row noMargin">
        <h2>{makesOptions?.length <= 2 ? t('dashboardCharts.select_trim_duration') : t('dashboardCharts.select_brand_duration')}</h2>
      </ListHeader>

      <div className="dashboard-header" style={{ width: '100%' }}>
        <div className="inputs_grid small-gap">
          {makesOptions?.length <= 2 ? null : makesApiDataLoading ? (
            <InputSkeleton noHeader gridSize={4} />
          ) : (
            <Select
              value={makesOptions.find((item: any) => item?.value === selectedBrand)}
              placeholder={hasPermission(['makes.show']) ? t('dashboardCharts.select_brand') : t('no_permission_to_show')}
              isDisabled={!hasPermission(['makes.show'])}
              isSearchable={false}
              isClearable={false}
              isMulti={false}
              options={makesOptions}
              className="select-drop-down grid-4"
              noOptionsMessage={() => t('no_options')}
              classNamePrefix="react-select"
              styles={customSelectStyles as any}
              onChange={(option) => {
                setSelectedBrand(option?.value as string);
              }}
            />
          )}
          {modelsApiDataLoading ? (
            <InputSkeleton noHeader gridSize={4} />
          ) : (
            <Select
              value={modelsOptions.find((item: any) => item?.value === selectedModel)}
              placeholder={hasPermission(['models.show']) ? t('model') : t('no_permission_to_show')}
              isDisabled={!hasPermission(['models.show'])}
              isSearchable={false}
              isClearable={false}
              isMulti={false}
              options={modelsOptions}
              className="select-drop-down grid-4"
              styles={customStyles as any}
              onChange={(option) => {
                setSelectedModel(option?.value as string);
              }}
            />
          )}
          {variantsApiDataLoading ? (
            <InputSkeleton noHeader gridSize={4} />
          ) : (
            <Select
              value={variantsOptions.find((item: any) => item?.value === selectedVariant)}
              placeholder={hasPermission(['variants.show']) ? t('variant') : t('no_permission_to_show')}
              isDisabled={!hasPermission(['variants.show'])}
              isSearchable={false}
              isClearable={false}
              isMulti={false}
              options={variantsOptions}
              className="select-drop-down grid-4"
              styles={customStyles as any}
              onChange={(option) => {
                setSelectedVariant(option?.value as string);
              }}
            />
          )}
        </div>

        <DurationTabs t={t} duration setOpenModal={setOpenModal} tabs={dashboardDurations} activeTab={activeTab} setActiveTab={setActiveTab} setDateRange={setDateRange} />
      </div>

      <SalesData salesData={salesData} purchasesVehiclesData={purchasesVehiclesData} />
      <div className='charts-row'>
        <VehiclesData salesPerModelData={salesPerModelData} />
        <MonthlySales monthlySalesData={monthlySalesData} />
      </div>
      <SalesUsersData salesUsersData={salesUsersData} selectedDuration={selectedDuration} />

      {openModal && (
        <ModalContainer small customClass="x-small">
          <CustomizeDateRange setDateRange={setDateRange} dateRange={dateRange} setActiveTab={setActiveTab} setOpenModal={setOpenModal} />
        </ModalContainer>
      )}
    </div>
  );
};

export default Dashboard;
